/*! gwfine frame reconstruct — generated by scripts/build-frame-reconstruct.js — do not edit by hand */
(function (root) {
/**
 * Draw a picture frame from one straight moulding sample.
 *
 * Atlas: left square is unused at runtime. Right strip is one rail
 * (outer at the top of the image, inner at the bottom), baked tileable
 * along its length so it can be extruded without a hard seam.
 *
 * Method: sweep the moulding cross-section into one stick long enough
 * for the outer perimeter (stable face profile + looping fine grain),
 * then cut four 45° miters and join them clockwise from the top.
 * Adjacent sides are sequential cuts of that stick, not four copies.
 *
 * Works in the browser (CanvasRenderingContext2D) and is bundled to
 * assets/gw-frame-reconstruct.js for the PDP and wall visualizer.
 * Booth-config requires this module and paints a CanvasTexture.
 */
const stickCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null;

function atlasParts(atlas) {
  if (!atlas) return null;
  const height = atlas.naturalHeight || atlas.height || 0;
  const width = atlas.naturalWidth || atlas.width || 0;
  if (!height || width <= height) return null;
  return { cornerSize: height, railX: height, railW: width - height, railH: height };
}

function readyAtlas(atlas) {
  return !!(atlas && (atlas.naturalWidth || atlas.width) && (atlas.complete !== false));
}

function stickLength(width, height) {
  return 2 * (width + height);
}

function stickCuts(width, height) {
  return [
    { along0: 0, length: width },
    { along0: width, length: height },
    { along0: width + height, length: width },
    { along0: width + height + width, length: height },
  ];
}

function railTileWidth(parts, face) {
  if (!parts || !parts.railH) return 0;
  return (parts.railW / parts.railH) * face;
}

function clipPolygon(ctx, points) {
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i += 1) ctx.lineTo(points[i][0], points[i][1]);
  ctx.closePath();
  ctx.clip();
}

function medianByte(values) {
  const sorted = values.slice().sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)] || 0;
}

function paintTiledStick(ctx, atlas, parts, stickLen, face) {
  const tileW = railTileWidth(parts, face);
  if (tileW <= 0.5) return;
  let x = 0;
  while (x < stickLen) {
    ctx.drawImage(
      atlas,
      parts.railX,
      0,
      parts.railW,
      parts.railH,
      x,
      0,
      tileW + 0.75,
      face
    );
    x += tileW;
  }
}

function extrudeStick(atlas, parts, stickLen, face) {
  const tileW = Math.max(1, Math.round(railTileWidth(parts, face)));
  const h = Math.max(1, Math.ceil(face));
  const w = Math.max(1, Math.ceil(stickLen));
  const sample = document.createElement('canvas');
  sample.width = tileW;
  sample.height = h;
  const sampleCtx = sample.getContext('2d');
  if (!sampleCtx) return null;
  sampleCtx.imageSmoothingEnabled = true;
  sampleCtx.drawImage(atlas, parts.railX, 0, parts.railW, parts.railH, 0, 0, tileW, h);
  let src;
  try {
    src = sampleCtx.getImageData(0, 0, tileW, h).data;
  } catch (err) {
    return null;
  }

  const profile = new Uint8ClampedArray(h * 4);
  for (let y = 0; y < h; y += 1) {
    for (let k = 0; k < 4; k += 1) {
      const values = new Array(tileW);
      for (let x = 0; x < tileW; x += 1) values[x] = src[(y * tileW + x) * 4 + k];
      profile[y * 4 + k] = medianByte(values);
    }
  }

  const radius = Math.max(4, Math.round(tileW * 0.12));
  const grainMix = 0.78;
  const prefix = new Float64Array((tileW + 1) * 4);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const img = ctx.createImageData(w, h);
  const dst = img.data;

  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < tileW; x += 1) {
      const i = (y * tileW + x) * 4;
      const p = (x + 1) * 4;
      prefix[p] = prefix[p - 4] + src[i];
      prefix[p + 1] = prefix[p - 3] + src[i + 1];
      prefix[p + 2] = prefix[p - 2] + src[i + 2];
      prefix[p + 3] = prefix[p - 1] + src[i + 3];
    }
    for (let x = 0; x < w; x += 1) {
      const sx = x % tileW;
      const x0 = Math.max(0, sx - radius);
      const x1 = Math.min(tileW, sx + radius + 1);
      const n = x1 - x0;
      const si = (y * tileW + sx) * 4;
      const di = (y * w + x) * 4;
      const po = y * 4;
      for (let k = 0; k < 4; k += 1) {
        const blurred = (prefix[x1 * 4 + k] - prefix[x0 * 4 + k]) / n;
        const grain = (src[si + k] - blurred) * grainMix;
        const value = profile[po + k] + grain;
        dst[di + k] = value < 0 ? 0 : value > 255 ? 255 : Math.round(value);
      }
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

function getStick(atlas, parts, stickLen, face) {
  if (typeof document === 'undefined' || !document.createElement) return null;
  const w = Math.max(1, Math.ceil(stickLen));
  const h = Math.max(1, Math.ceil(face));
  const prev = stickCache ? stickCache.get(atlas) : null;
  if (prev && prev.w === w && prev.h === h) return prev.canvas;
  const extruded = extrudeStick(atlas, parts, w, h);
  const canvas = extruded || document.createElement('canvas');
  if (!extruded) {
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    if (ctx.imageSmoothingQuality) ctx.imageSmoothingQuality = 'high';
    paintTiledStick(ctx, atlas, parts, w, h);
  }
  if (stickCache) stickCache.set(atlas, { w, h, canvas });
  return canvas;
}

function drawCut(ctx, atlas, parts, stick, along0, length, face) {
  if (length <= 0 || face <= 0) return;
  if (stick) {
    ctx.drawImage(stick, along0, 0, length, stick.height, 0, 0, length, face);
    return;
  }
  const tileW = railTileWidth(parts, face);
  if (tileW <= 0.5) return;
  const startTile = Math.floor(along0 / tileW);
  const endTile = Math.ceil((along0 + length) / tileW);
  for (let t = startTile; t < endTile; t += 1) {
    ctx.drawImage(
      atlas,
      parts.railX,
      0,
      parts.railW,
      parts.railH,
      t * tileW - along0,
      0,
      tileW + 0.75,
      face
    );
  }
}

/**
 * Draw a reconstructed moulding into an axis-aligned rectangle.
 * @param {CanvasRenderingContext2D} ctx
 * @param {*} atlas Image or canvas
 * @param {number} width outer width in canvas units
 * @param {number} height outer height in canvas units
 * @param {number} face moulding face width in canvas units
 * @param {number} [x]
 * @param {number} [y]
 */
function drawReconstructedFrame(ctx, atlas, width, height, face, x, y) {
  const ox = x || 0;
  const oy = y || 0;
  if (!ctx || width <= 0 || height <= 0 || face <= 0) return false;
  const parts = readyAtlas(atlas) ? atlasParts(atlas) : null;
  if (!parts) return false;

  ctx.save();
  ctx.translate(ox, oy);
  ctx.imageSmoothingEnabled = true;
  if (ctx.imageSmoothingQuality) ctx.imageSmoothingQuality = 'high';

  const cuts = stickCuts(width, height);
  const stick = getStick(atlas, parts, stickLength(width, height), face);
  const sides = [
    {
      clip: [
        [0, 0],
        [width, 0],
        [width - face, face],
        [face, face],
      ],
      setup: function () {},
    },
    {
      clip: [
        [width, 0],
        [width, height],
        [width - face, height - face],
        [width - face, face],
      ],
      setup: function () {
        ctx.translate(width, 0);
        ctx.rotate(Math.PI / 2);
      },
    },
    {
      clip: [
        [face, height - face],
        [width - face, height - face],
        [width, height],
        [0, height],
      ],
      setup: function () {
        ctx.translate(width, height);
        ctx.rotate(Math.PI);
      },
    },
    {
      clip: [
        [0, 0],
        [face, face],
        [face, height - face],
        [0, height],
      ],
      setup: function () {
        ctx.translate(0, height);
        ctx.rotate(-Math.PI / 2);
      },
    },
  ];

  for (let i = 0; i < sides.length; i += 1) {
    ctx.save();
    clipPolygon(ctx, sides[i].clip);
    sides[i].setup();
    drawCut(ctx, atlas, parts, stick, cuts[i].along0, cuts[i].length, face);
    ctx.restore();
  }

  ctx.restore();
  return true;
}

function fillFallbackFrame(ctx, width, height, face, rgb, x, y) {
  const ox = x || 0;
  const oy = y || 0;
  const r = (rgb && rgb[0]) || 40;
  const g = (rgb && rgb[1]) || 38;
  const b = (rgb && rgb[2]) || 36;
  ctx.save();
  ctx.translate(ox, oy);
  ctx.fillStyle = 'rgb(' + r + ',' + g + ',' + b + ')';
  ctx.beginPath();
  ctx.rect(0, 0, width, height);
  ctx.rect(face, face, Math.max(0, width - face * 2), Math.max(0, height - face * 2));
  ctx.fill('evenodd');
  ctx.restore();
}

const FrameReconstruct = {
  atlasParts,
  readyAtlas,
  stickLength,
  stickCuts,
  drawReconstructedFrame,
  fillFallbackFrame,
};


  root.GWFrameReconstruct = FrameReconstruct;
})(typeof window !== 'undefined' ? window : globalThis);
